import { jobPositionStore } from 'ats/pages/job-positions/JobPositionStore';
import {
	deserializeColumnState,
	serializeColumnState,
	normalizeColumnStateForCompare
} from './columnState';
import {
	getCurrentUser,
	getCurrentUserId,
	normalizeUserRefForCompare,
	resolveUserRefs,
	serializeUserRefs
} from './userRef';
import { resolveEntityRefs, serializeEntityRefs } from './entityRef';
import {
	deserializeDateRange,
	normalizeCreatableMultiValue,
	normalizeEntityRefIds,
	normalizeQueryState,
	normalizeUiState,
	pickViewQueryForView,
	serializeDateRange,
	serializeOptionValues,
	captureSearchFilter,
	resolveSearchFilterForApply
} from './viewStateUtils';
import { syncFilterDrawerProps } from './syncFilterDrawerProps';

const EMPTY_QUERY = { sort: { column: 'createdAt', asc: false } };

const JOBS_VIEW_QUERY_KEYS = [
	'title',
	'text',
	'status',
	'clientId',
	'createdAt',
	'urgency',
	'priority',
	'disabled',
	'owner',
	'hiringManager',
	'accountManager',
	'timezones',
	'id',
	'mineOnly'
];

const JOBS_ENTITY_REF_KEYS = ['clientIds', 'hiringManagerIds', 'timezoneIds'];
const JOBS_USER_REF_KEYS = ['ownerFilter', 'accountManagerFilter'];

function pickJobsQueryForView(query) {
	return pickViewQueryForView(query, JOBS_VIEW_QUERY_KEYS, EMPTY_QUERY.sort);
}

function getDefaultStatusFilter() {
	return {
		...jobPositionStore.getStatusByValue('open'),
		label: 'Status: Open'
	};
}

export function getJobsFactoryUiState() {
	return {
		searchFilter: null,
		statusFilter: getDefaultStatusFilter(),
		clientFilter: [],
		createdOnFilter: null,
		urgencyFilter: null,
		priorityFilter: null,
		disabledFilter: null,
		ownerFilter: null,
		hiringManagerFilter: null,
		accountManagerFilter: null,
		timezoneFilter: null,
		jobIdFilter: [],
		mineOnly: false
	};
}

export function captureJobsListView(page) {
	const currentUserId = getCurrentUserId();

	return {
		ui: {
			searchFilter: captureSearchFilter(
				page.state.searchFilter,
				page.listStore.store.query
			),
			statusFilter: page.state.statusFilter?.value ?? null,
			clientIds: serializeEntityRefs(page.state.clientFilter),
			createdOnFilter: serializeDateRange(page.state.createdOnFilter),
			urgencyFilter: page.state.urgencyFilter?.value ?? null,
			priorityFilter: serializeOptionValues(page.state.priorityFilter),
			disabledFilter: page.state.disabledFilter,
			ownerFilter: serializeUserRefs(page.state.ownerFilter, currentUserId),
			hiringManagerIds: serializeEntityRefs(page.state.hiringManagerFilter),
			accountManagerFilter: serializeUserRefs(
				page.state.accountManagerFilter,
				currentUserId
			),
			timezoneIds: serializeEntityRefs(page.state.timezoneFilter, 'text'),
			jobIdFilter: page.state.jobIdFilter || null,
			mineOnly: !!page.state.mineOnly
		},
		query: pickJobsQueryForView(page.listStore.store.query),
		columnState: serializeColumnState(page.state.visibleColumns)
	};
}

export function getJobsFactorySnapshot(page) {
	return {
		ui: {
			searchFilter: null,
			statusFilter: 'open',
			clientIds: null,
			createdOnFilter: null,
			urgencyFilter: null,
			priorityFilter: null,
			disabledFilter: null,
			ownerFilter: null,
			hiringManagerIds: null,
			accountManagerFilter: null,
			timezoneIds: null,
			jobIdFilter: null,
			mineOnly: false
		},
		query: {
			sort: { column: 'createdAt', asc: false },
			status: 'open'
		},
		columnState: serializeColumnState(
			jobPositionStore.defaultTableColumnsOptions
		)
	};
}

function buildUiStateFromSaved(ui) {
	const currentUser = getCurrentUser();

	return {
		searchFilter: ui.searchFilter || null,
		statusFilter:
			ui.statusFilter == null
				? null
				: ui.statusFilter
					? {
							...jobPositionStore.getStatusByValue(ui.statusFilter),
							label: 'Status: Open'
						}
					: null,
		clientFilter: resolveEntityRefs(ui.clientIds) || [],
		createdOnFilter: deserializeDateRange(ui.createdOnFilter),
		urgencyFilter: ui.urgencyFilter
			? {
					...jobPositionStore.getUrgencyByValue(ui.urgencyFilter),
					label: jobPositionStore.getUrgencyByValue(ui.urgencyFilter)?.text
				}
			: null,
		priorityFilter:
			ui.priorityFilter
				?.map(value => {
					const option = jobPositionStore.priorityOptions.find(
						priority => priority.value === value
					);
					return option ? { ...option, label: option.text } : null;
				})
				.filter(Boolean) || null,
		disabledFilter: ui.disabledFilter ?? null,
		ownerFilter: resolveUserRefs(ui.ownerFilter, currentUser),
		hiringManagerFilter: resolveEntityRefs(ui.hiringManagerIds) || null,
		accountManagerFilter: resolveUserRefs(ui.accountManagerFilter, currentUser),
		timezoneFilter: resolveEntityRefs(ui.timezoneIds, 'text') || null,
		jobIdFilter: ui.jobIdFilter || [],
		mineOnly: !!ui.mineOnly
	};
}

export function applyJobsListView(
	page,
	snapshot,
	{ isFactoryDefault = false } = {}
) {
	const ui = buildUiStateFromSaved(snapshot.ui || {});
	ui.searchFilter = resolveSearchFilterForApply(
		ui.searchFilter,
		snapshot.query
	);
	const visibleColumns = isFactoryDefault
		? jobPositionStore.defaultTableColumnsOptions
		: deserializeColumnState(
				snapshot.columnState,
				jobPositionStore.tableColumnsOptions,
				jobPositionStore.defaultTableColumnsOptions
			);

	page.listStore.store.resetQuery();
	page.listStore.store.setQuery(
		'sort',
		snapshot.query?.sort || { column: 'createdAt', asc: false }
	);

	page.listStore.store.setQuery('title', ui.searchFilter || null);
	if (snapshot.query?.status != null) {
		page.listStore.store.setQuery('status', snapshot.query.status);
	} else if (isFactoryDefault) {
		page.listStore.store.setQuery('status', ['open']);
	}
	if (snapshot.query?.clientId)
		page.listStore.store.setQuery('clientId', snapshot.query.clientId);
	if (snapshot.query?.createdAt)
		page.listStore.store.setQuery('createdAt', snapshot.query.createdAt);
	if (snapshot.query?.urgency != null)
		page.listStore.store.setQuery('urgency', snapshot.query.urgency);
	if (snapshot.query?.priority)
		page.listStore.store.setQuery('priority', snapshot.query.priority);
	if (snapshot.query?.disabled != null)
		page.listStore.store.setQuery('disabled', snapshot.query.disabled);
	if (snapshot.query?.owner)
		page.listStore.store.setQuery('owner', snapshot.query.owner);
	if (snapshot.query?.hiringManager) {
		page.listStore.store.setQuery(
			'hiringManager',
			snapshot.query.hiringManager
		);
	}
	if (snapshot.query?.accountManager) {
		page.listStore.store.setQuery(
			'accountManager',
			snapshot.query.accountManager
		);
	}
	if (snapshot.query?.timezones)
		page.listStore.store.setQuery('timezones', snapshot.query.timezones);
	if (snapshot.query?.id)
		page.listStore.store.setQuery('id', snapshot.query.id);
	page.listStore.store.setQuery('mineOnly', ui.mineOnly ? true : null);

	return new Promise(resolve => {
		page.setState({ ...ui, visibleColumns }, () => {
			syncFilterDrawerProps(page);
			page.handlePaginationChange(1);
			resolve();
		});
	});
}

export function applyJobsFactoryDefaults(page) {
	return applyJobsListView(page, getJobsFactorySnapshot(page), {
		isFactoryDefault: true
	});
}

export function resolveJobsViewSnapshot(view) {
	if (view.systemKey === 'default') {
		return getJobsFactorySnapshot();
	}
	return {
		ui: view.filterState?.ui || {},
		query: view.filterState?.query || EMPTY_QUERY,
		columnState: view.columnState
	};
}

export function normalizeJobsSnapshot(snapshot) {
	const ui = normalizeUiState(snapshot.ui || {});

	JOBS_ENTITY_REF_KEYS.forEach(key => {
		if (ui[key] != null) {
			ui[key] = normalizeEntityRefIds(ui[key]);
		}
	});
	JOBS_USER_REF_KEYS.forEach(key => {
		if (ui[key] != null) {
			ui[key] = normalizeUserRefForCompare(ui[key]);
		}
	});
	if (ui.jobIdFilter != null) {
		ui.jobIdFilter = normalizeCreatableMultiValue(ui.jobIdFilter);
	}

	return {
		ui,
		query: normalizeQueryState(
			pickJobsQueryForView(snapshot.query || {}),
			EMPTY_QUERY.sort
		),
		columnState: normalizeColumnStateForCompare(
			snapshot.columnState,
			jobPositionStore.defaultTableColumnsOptions
		)
	};
}

export function serializeJobsViewForSave(snapshot) {
	return {
		filterState: {
			ui: snapshot.ui,
			query: snapshot.query
		},
		columnState: snapshot.columnState
	};
}
